import React from "react";
import { PageLayout } from "../components/PageLayout";
import parse from "html-react-parser";
import { pageContent } from "../page-content";
import { LazyLoadImage } from "react-lazy-load-image-component";

import "../styles/app.scss";
import "react-lazy-load-image-component/src/effects/blur.css";
// import { NewsletterSignup } from "src/components/NewsletterSignup";
import { OutboundLink } from "src/components/OutboundLink";
import { VideoContainer } from "src/components/Video";
import classnames from "classnames";
import { SocialShareButtons } from "src/components/SocialShareButtons";
import widont from "widont";
import IframeComponent from "src/components/IframeComponent";

type Author = {
  name: string;
  url: string;
};

const getDatePublished = () => {
  const timestamp = process.env.REACT_APP_PUB_DATE;
  if (!timestamp) {
    throw new Error("No publication date defined in .env file!");
  } else {
    const date = new Date(timestamp.replace(/-/g, "/"));
    const dateFormatted = date.toLocaleDateString("en-us", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return dateFormatted;
  }
};

const convertToHtml = (text: string) => {
  let formattedText = text;

  // Make links outbound:
  formattedText = formattedText.replace(
    "<a href=",
    '<a target="_blank" rel="noopener noreferrer" href='
  );

  // Fix double spaces and non-spaced commas:
  formattedText = formattedText.replace("  ", " ").replace(",", ", ");

  return parse(formattedText);
};

type Slide = {
  slideContent: string;
  photoFileName?: string;
  photoPosition?: string;
};

const Paragraph: React.FC<{ text: string }> = ({ text }) => (
  <p className="copy">{convertToHtml(text)}</p>
);

export const splitParagraphs = (content: string) =>
  content.split("{newParagraph}");

export const formatContent = (content: string) => (
  <>
    {splitParagraphs(content).map((paragraph, i) => (
      <Paragraph key={i} text={paragraph} />
    ))}
  </>
);

const { slideX, slide0, ...slideContent } = pageContent;

const introSlide = slide0 as Slide;
const slides = Object.entries(slideContent).map((entry) => entry[1]) as Slide[];

export const Homepage = () => {
  const byline = JSON.parse(process.env.REACT_APP_AUTHOR as string) as Author[];

  return (
    <PageLayout>
      <div className="app">
        <div className="scrolly-container">
          <div className="scrolly-image">
            <LazyLoadImage
              alt=""
              height="100vh"
              src={require(`../assets/images/scrolly-photos/${introSlide.photoFileName}`)}
              width="100%"
              effect="blur"
              style={{
                objectPosition: introSlide.photoPosition || "50% 50%",
              }}
            />
          </div>

          <div
            className="hero is-fullheight is-flex is-flex-direction-column is-justify-content-flex-end"
          >
            <div className="landing-content mx-5">
              <h1 className="headline mb-0">{widont("12 Hours in FiDi")}</h1>
              <div className="deck mt-1 mb-0"></div>
              <div className="attribution mt-0">
                <p className="byline mt-1">
                  By{" "}
                  {byline.map((author, i) => (
                    <span key={i} className="author">
                      <OutboundLink to={author.url}>{author.name}</OutboundLink>
                      {i < byline.length - 2
                        ? ", "
                        : i < byline.length - 1
                        ? " and "
                        : ""}
                    </span>
                  ))}
                  {" | "}
                  {getDatePublished()}
                  <br /> Design and Development by{" "}
                  <OutboundLink to="https://www.thecity.nyc/authors/sam-rabiyah">
                    {widont("Sam Rabiyah")}
                  </OutboundLink>
                  <OutboundLink to="https://www.thecity.nyc/authors/sujin-shin">
                    {widont(" & Sujin Shin")}
                  </OutboundLink>
                </p>
              </div>
              <SocialShareButtons />
            </div>
          </div>

          <div
                className="title-page-text"
                style={{
                  backgroundColor: "#556c91d8",
                  zIndex: "2",
                  fontFamily: "Georgia, Sans Serif",
                  fontWeight: "600",
                  fontSize:"1.3em",
                  position: "absolute",
                  paddingTop:"1em",
                  paddingBottom:"1em",
                  paddingLeft:"1em",
                  paddingRight:"1em"
                }}
              >
                <p>
                Where New Yorkers work, live and play is up for grabs — especially since the pandemic unsettled familiar patterns — with the character of our communities changing in front of our eyes. To capture a city in flux, THE CITY is embarking on a new multimedia project, exploring 12 hours in 12 different neighborhoods. 
                  <br />
                  <br />
                  Our first stop is the Financial District. An area once defined by Wall Street workers has seen an influx of young professionals seeking residential space in converted office buildings. We found young families, college students and professionals in their 20s speaking about how FiDi feels like a world unto itself, detached from the frantic pace of other Manhattan neighborhoods.
                </p>
              </div>

          <div style={{display:"flex", justifyContent:"center"}}>
            <div
            className="first-caption"
              style={{
                color: "white",
                fontSize: "1.5em",
                position: "absolute",
                zIndex: "2",
                backgroundColor: "#556c91d8",
                maxWidth: "30em",
                marginLeft: "40px",
                fontFamily: "Georgia, Sans Serif",
                paddingRight:"20px",
                paddingLeft:"20px",
                paddingTop:"20px",
                paddingBottom:"20px",
                marginRight:"40px"
              }}
            >
              <p>
              “I see more strollers now than briefcases,” said Harish Kamath while walking his 4-year-old son Raey to school just after dawn. Kamath and family moved into the area in 2019.
              </p>
            </div>
          </div>
        </div>

        {/* <NewsletterSignup /> */}

        {slides.map((slide, i) => {
          const { slideContent, photoFileName, photoPosition } = slide;
          const isPhotoPulledLeft = photoFileName === "gorgeous_george_1.jpg";

          // CASE 1: No Photo
          if (!photoFileName) {
            return (
              <React.Fragment key={i}>
                <IframeComponent></IframeComponent>
              </React.Fragment>
            );
          }

          // CASE 2: VIDEO
          if (
            photoFileName.startsWith(
              "https://s3.amazonaws.com/static.thecity.nyc/video/"
            )
          ) {
            return (
              <VideoContainer
                videoUrl={photoFileName}
                slideContent={slideContent}
                key={i}
              />
            );
          }

          // CASE 3: PHOTO
          return (
            <div
              key={i}
              className={classnames(
                "scrolly-container",
                "is-flex",
                "is-flex-direction-column",
                "is-justify-content-space-between",
                isPhotoPulledLeft
                  ? "photo-pulled-left"
                  : "is-align-items-center",
                splitParagraphs(slideContent).length > 1 && "is-doubled"
              )}
              style={{
                height: `${
                  200 + (splitParagraphs(slideContent).length || 1) * 100
                }vh`,
              }}
            >
              <div className="scrolly-image">
                <LazyLoadImage
                  alt=""
                  height="100vh"
                  src={require(`../assets/images/scrolly-photos/${photoFileName}`)}
                  width="100%"
                  effect="blur"
                  style={{
                    objectPosition: photoPosition || "50% 50%",
                  }}
                />
              </div>
              {splitParagraphs(slideContent).map((slideText, i) => (
                <div
                  className="scrolly-caption is-flex-direction-column is-justify-content-center p-3 mx-2"
                  style={{
                    marginTop: `${i + 1}00vh`,
                    fontFamily: "Georgia, Sans Serif",
                    fontWeight: slideText.length < 9 ? "bolder" : "",
                    fontSize: slideText.length < 9 ? "3em" : "1.5em",
                    backgroundColor: slideText.length < 1 ? "" : "#556c91d8",
                  }}
                  key={i}
                >
                  <Paragraph text={slideText} />
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {/* <IframeComponent></IframeComponent> */}
    </PageLayout>
  );
};
